import { useEffect, useRef } from 'react';

/**
 * Subscribes a per-frame callback for the lifetime of the host
 * component. The callback receives the current `performance.now()`
 * timestamp so it can compute elapsed ms, throttle, or skip frames
 * cheaply.
 *
 * Why this exists: every canvas drawer in the app (TunerView,
 * GestureHUD, StringsOverlay, HandOverlay, CalibrationWizard) was
 * reinventing the same `let raf = requestAnimationFrame(tick); return
 * () => cancelAnimationFrame(raf)` boilerplate, sometimes with subtle
 * bugs (forgetting cancel-on-cleanup, recreating the loop on every
 * dep change). Centralising it removes ~10 LOC × 5 sites and gives us
 * one place to add frame-budget metering or shared scheduling later.
 *
 * The callback is held in a ref so consumers can close over fresh
 * state without restarting the loop. Pass `enabled = false` to pause
 * (useful when a feature is hidden / behind a drawer).
 */
export function useRafLoop(
  callback: (now: number) => void,
  enabled = true,
): void {
  const fnRef = useRef(callback);
  fnRef.current = callback;

  useEffect(() => {
    if (!enabled) return;
    let raf = 0;
    let cancelled = false;
    const tick = (now: number) => {
      if (cancelled) return;
      fnRef.current(now);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [enabled]);
}
